/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   10/11/2022
 */

import React from "react";
import { Autocomplete, Checkbox, FormControl, FormControlLabel, Stack, TextField } from "@mui/material";
import { UseFormReturnType } from "@mantine/form";
import { SummaryNotificationConfig } from "../../interfaces/Global";
import { format } from "../../dates-helper";

export interface ConfigScheduleProps {
	form: UseFormReturnType<SummaryNotificationConfig>;
}

export const daysOfWeek = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

export default function ConfigSchedule(props: ConfigScheduleProps) {

	return (
		<Stack spacing={2} mb={2}>
			<DayOfWeekSelector {...props} />
			<TimeOfDaySelector {...props} />
		</Stack>
	);

}

function DayOfWeekSelector({ form }: ConfigScheduleProps) {

	return (
		<Stack direction={"row"} spacing={1}>
			{daysOfWeek.map(dow =>
				<FormControl key={dow} variant={"standard"}>
					<FormControlLabel
						control={
							<Checkbox
								checked={form.values.daysOfWeek.includes(dow)}
								onChange={(event) => {
									if (event.target.checked) {
										form.insertListItem("daysOfWeek", dow);
									} else {
										form.removeListItem(
											"daysOfWeek",
											form.values.daysOfWeek.findIndex(i => i === dow)
										);
									}
								}}
							/>
						}
						label={dow}
					/>
				</FormControl>)}
		</Stack>
	)
}

function TimeOfDaySelector({ form }: ConfigScheduleProps) {

	const times: string[] = [];
	for (let i=0; i<24; i++) {
		times.push(String(i < 10 ? "0" + i : i) + ":00");
		times.push(String(i < 10 ? "0" + i : i) + ":30");
	}

	return (
		<Stack direction={"row"} spacing={1}>
			<Stack flexGrow={1}>
				<Autocomplete
					multiple
					options={times}
					value={form.values.times}
					onChange={(event, times) => form.setFieldValue('times', times)}
					ChipProps={{
						size: "small"
					}}
					getOptionLabel={(t) => {
						const [h,m] = t.split(":");
						const date = new Date();
						date.setHours(Number(h));
						date.setMinutes(Number(m));
						return String(format(date, 'p'));
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							variant="standard"
							label="Times of Day"
						/>
					)}
				/>
			</Stack>
			<Stack width={300}>
				<Autocomplete
					options={Intl.supportedValuesOf('timeZone')}
					value={form.values.timeZone}
					onChange={(event, tz) => form.setFieldValue('timeZone', tz || "UTC")}
					renderInput={(params) => (
						<TextField
							{...params}
							variant="standard"
							label="Time Zone"
						/>
					)}
				/>
			</Stack>
		</Stack>
	);
}